import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import api from '../services/api';
import WebInvoicesTab from '../components/WebInvoicesTab';
import ContactModal from '../components/ContactModal';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  DocumentTextIcon,
  CurrencyDollarIcon,
  PlusIcon,
  XMarkIcon,
  PhoneIcon,
  GlobeAltIcon,
  MapPinIcon,
  StarIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowRightIcon,
  ChevronDownIcon,
  BuildingStorefrontIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  TrashIcon,
  PencilIcon,
  EyeIcon,
  ChartBarIcon,
  CalculatorIcon,
  CheckIcon,
  BoltIcon,
  BanknotesIcon
} from '@heroicons/react/24/outline';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

// Pricing Configuration
const PRICING_TIERS = {
  starter: {
    name: 'Starter',
    description: 'Perfect for small businesses getting online',
    setupFee: 1000,
    monthlyFee: 49,
    pages: '1-5 pages',
    editsPerMonth: 2,
    features: ['Mobile Responsive', 'Contact Form', 'Google Maps', 'Basic SEO', 'SSL Certificate', '2 Edits/Month'],
    buildTime: '3-5 days',
    color: '#3b82f6',
    recommended: []
  },
  professional: {
    name: 'Professional',
    description: 'Great for growing businesses',
    setupFee: 1500,
    monthlyFee: 79,
    pages: '5-10 pages',
    editsPerMonth: 4,
    features: ['Everything in Starter', 'Photo Gallery', 'Service Menu/Pricing', 'Social Media Integration', 'Reviews Section', 'Blog Setup', '4 Edits/Month'],
    buildTime: '1-2 weeks',
    color: '#8b5cf6',
    recommended: ['restaurants', 'salons', 'contractors']
  },
  premium: {
    name: 'Premium',
    description: 'Full-featured business website',
    setupFee: 2500,
    monthlyFee: 129,
    pages: '10+ pages',
    editsPerMonth: 'Unlimited',
    features: ['Everything in Professional', 'Online Booking/Scheduling', 'Custom Forms', 'Advanced SEO', 'Analytics Dashboard', 'Priority Support', 'Unlimited Edits'],
    buildTime: '2-4 weeks',
    color: '#10b981',
    recommended: ['medical', 'legal', 'real_estate']
  }
};

// Prepaid Hosting Option
const PREPAID_HOSTING = {
  upfrontCost: 250,
  monthlyDiscount: 25,
  description: 'Pay $250 upfront, save $25/mo on hosting'
};

const ADDON_FEATURES = [
  { id: 'booking', name: 'Online Booking System', setupFee: 300, monthlyFee: 20, description: 'Let customers book appointments 24/7' },
  { id: 'ecommerce', name: 'E-Commerce (up to 50 products)', setupFee: 500, monthlyFee: 30, description: 'Sell products online with cart & checkout' },
  { id: 'chat', name: 'Live Chat Widget', setupFee: 0, monthlyFee: 15, description: 'Chat with visitors in real-time' },
  { id: 'email', name: 'Email Marketing Setup', setupFee: 200, monthlyFee: 0, description: 'Mailchimp/newsletter integration' },
  { id: 'logo', name: 'Logo Design', setupFee: 250, monthlyFee: 0, description: 'Professional logo with 3 concepts' },
  { id: 'photos', name: 'Stock Photos Package', setupFee: 100, monthlyFee: 0, description: '20 premium stock photos' },
  { id: 'copywriting', name: 'Professional Copywriting', setupFee: 300, monthlyFee: 0, description: 'SEO-optimized content writing' },
  { id: 'gmb', name: 'Google Business Setup', setupFee: 150, monthlyFee: 0, description: 'Optimize Google Business Profile' },
  { id: 'maintenance', name: 'Extra Edits (5/month)', setupFee: 0, monthlyFee: 25, description: 'Additional monthly content updates' },
  { id: 'analytics', name: 'Monthly Analytics Report', setupFee: 0, monthlyFee: 20, description: 'Detailed traffic & conversion reports' }
];

const BUSINESS_PRESETS = {
  churches: { tier: 'starter', addons: ['photos'], discount: 10 },
  restaurants: { tier: 'professional', addons: ['photos', 'gmb'], discount: 0 },
  contractors: { tier: 'professional', addons: ['gmb', 'photos'], discount: 0 },
  salons: { tier: 'professional', addons: ['booking'], discount: 0 },
  medical: { tier: 'premium', addons: ['booking', 'chat'], discount: 0 },
  legal: { tier: 'premium', addons: ['chat', 'copywriting'], discount: 0 },
  gyms: { tier: 'professional', addons: ['booking'], discount: 0 },
  auto_repair: { tier: 'professional', addons: ['gmb'], discount: 0 },
  pet_services: { tier: 'starter', addons: ['booking'], discount: 0 },
  real_estate: { tier: 'premium', addons: ['chat', 'email'], discount: 0 },
  default: { tier: 'starter', addons: [], discount: 0 }
};

const LeadFinder = () => {
  const { user, isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState('search');
  const [loading, setLoading] = useState(false);

  // Search tab state
  const [categories, setCategories] = useState([]);
  const [searchAreas, setSearchAreas] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedArea, setSelectedArea] = useState('');
  const [selectedProviders, setSelectedProviders] = useState(['google', 'foursquare']);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [apiUsage, setApiUsage] = useState([]);
  const [searching, setSearching] = useState(false);

  // State Filings search state
  const [stateFilingsResults, setStateFilingsResults] = useState([]);
  const [selectedStates, setSelectedStates] = useState(['NY', 'CT', 'OR']);
  const [daysBack, setDaysBack] = useState(30);
  const [searchingFilings, setSearchingFilings] = useState(false);
  const [selectedFilingsLeads, setSelectedFilingsLeads] = useState([]);

  // Pipeline tab state
  const [leads, setLeads] = useState([]);
  const [pipelineCounts, setPipelineCounts] = useState({});
  const [pipelineFilter, setPipelineFilter] = useState('all');
  const [pipelineCategoryFilter, setPipelineCategoryFilter] = useState('');
  const [pipelineSourceFilter, setPipelineSourceFilter] = useState('');
  const [pipelineStateFilter, setPipelineStateFilter] = useState('');

  // Quotes tab state
  const [quotes, setQuotes] = useState([]);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [quoteForm, setQuoteForm] = useState({
    services: [{ description: '', quantity: 1, unit_price: 0 }],
    discount_percent: 0,
    tax_percent: 0,
    valid_until: '',
    client_notes: ''
  });

  // Quote Calculator state
  const [showCalculator, setShowCalculator] = useState(false);
  const [calcTier, setCalcTier] = useState('starter');
  const [calcAddons, setCalcAddons] = useState([]);
  const [calcDiscount, setCalcDiscount] = useState(0);
  const [calcContractMonths, setCalcContractMonths] = useState(12);
  const [calcBusinessType, setCalcBusinessType] = useState('default');
  const [calcPrepaidHosting, setCalcPrepaidHosting] = useState(false);

  // Profit tab state
  const [profitData, setProfitData] = useState(null);
  const [conversionData, setConversionData] = useState(null);

  // Lead detail modal
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactingLead, setContactingLead] = useState(null);
  const [leadDetail, setLeadDetail] = useState(null);
  const [activityForm, setActivityForm] = useState({ activity_type: 'note_added', description: '' });

  // Search area modal
  const [showAreaModal, setShowAreaModal] = useState(false);
  const [areaForm, setAreaForm] = useState({
    name: '', state: '', lat: '', lng: '', radius_miles: 10, county: ''
  });

  // Quote Calculator Functions
  const applyBusinessPreset = (businessType) => {
    const preset = BUSINESS_PRESETS[businessType] || BUSINESS_PRESETS.default;
    setCalcTier(preset.tier);
    setCalcAddons(preset.addons);
    setCalcDiscount(preset.discount);
    setCalcBusinessType(businessType);
  };

  const toggleAddon = (addonId) => {
    if (calcAddons.includes(addonId)) {
      setCalcAddons(calcAddons.filter(id => id !== addonId));
    } else {
      setCalcAddons([...calcAddons, addonId]);
    }
  };

  const calculateQuotePricing = () => {
    const tier = PRICING_TIERS[calcTier];
    const selectedAddons = ADDON_FEATURES.filter(a => calcAddons.includes(a.id));
    
    const tierSetup = tier.setupFee;
    const tierMonthly = tier.monthlyFee;
    
    const addonsSetup = selectedAddons.reduce((sum, a) => sum + a.setupFee, 0);
    const addonsMonthly = selectedAddons.reduce((sum, a) => sum + a.monthlyFee, 0);
    
    // Calculate prepaid hosting impact
    const prepaidSetup = calcPrepaidHosting ? PREPAID_HOSTING.upfrontCost : 0;
    const monthlyDiscount = calcPrepaidHosting ? PREPAID_HOSTING.monthlyDiscount : 0;
    
    const totalSetup = tierSetup + addonsSetup + prepaidSetup;
    const totalMonthly = (tierMonthly + addonsMonthly) - monthlyDiscount;
    
    const discountAmount = (tierSetup + addonsSetup) * (calcDiscount / 100);
    const finalSetup = totalSetup - discountAmount;
    
    const contractValue = finalSetup + (totalMonthly * calcContractMonths);
    const annualRecurring = totalMonthly * 12;
    
    // Calculate savings from prepaid hosting
    const prepaidSavings = calcPrepaidHosting ? (PREPAID_HOSTING.monthlyDiscount * 12) - PREPAID_HOSTING.upfrontCost : 0;
    
    return {
      tier,
      selectedAddons,
      tierSetup,
      tierMonthly,
      addonsSetup,
      addonsMonthly,
      prepaidSetup,
      monthlyDiscount,
      totalSetup,
      totalMonthly,
      discountAmount,
      finalSetup,
      contractValue,
      annualRecurring,
      prepaidSavings
    };
  };

  const generateQuoteFromCalculator = () => {
    const pricing = calculateQuotePricing();
    const services = [];
    
    // Add tier as main service
    services.push({
      description: `${pricing.tier.name} Website Package (${pricing.tier.pages}, ${pricing.tier.editsPerMonth} edits/mo)`,
      quantity: 1,
      unit_price: pricing.tierSetup
    });
    
    // Add prepaid hosting if selected
    if (calcPrepaidHosting) {
      services.push({
        description: 'Prepaid Hosting Package (Annual)',
        quantity: 1,
        unit_price: PREPAID_HOSTING.upfrontCost
      });
    }
    
    // Add monthly maintenance (with discount if prepaid)
    const monthlyRate = calcPrepaidHosting 
      ? pricing.tierMonthly - PREPAID_HOSTING.monthlyDiscount 
      : pricing.tierMonthly;
    services.push({
      description: `Monthly Hosting & Maintenance${calcPrepaidHosting ? ' (Prepaid Discount Applied)' : ''}`,
      quantity: calcContractMonths,
      unit_price: monthlyRate
    });
    
    // Add addons
    pricing.selectedAddons.forEach(addon => {
      if (addon.setupFee > 0) {
        services.push({
          description: `${addon.name} (Setup)`,
          quantity: 1,
          unit_price: addon.setupFee
        });
      }
      if (addon.monthlyFee > 0) {
        services.push({
          description: `${addon.name} (Monthly)`,
          quantity: calcContractMonths,
          unit_price: addon.monthlyFee
        });
      }
    });
    
    setQuoteForm({
      services,
      discount_percent: calcDiscount,
      tax_percent: 0,
      valid_until: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      client_notes: `${pricing.tier.name} package includes: ${pricing.tier.features.join(', ')}. Build time: ${pricing.tier.buildTime}.${calcPrepaidHosting ? ' Prepaid hosting discount applied.' : ''}`
    });
    
    setShowCalculator(false);
    setShowQuoteModal(true);
  };

  // Fetch initial data
  useEffect(() => {
    fetchCategories();
    fetchSearchAreas();
    fetchApiUsage();
  }, []);

  // Fetch data based on active tab
  useEffect(() => {
    if (activeTab === 'pipeline') {
      fetchLeads();
      fetchPipelineCounts();
    } else if (activeTab === 'quotes') {
      fetchQuotes();
    } else if (activeTab === 'profit') {
      fetchProfitData();
      fetchConversionData();
    }
  }, [activeTab]);

  // Refetch leads when pipeline filters change
  useEffect(() => {
    if (activeTab === 'pipeline') {
      fetchLeads();
    }
  }, [pipelineFilter, pipelineCategoryFilter, pipelineSourceFilter, pipelineStateFilter]);

  const fetchCategories = async () => {
    try {
      const res = await api.get('/api/lead-finder/categories');
      setCategories(res.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchSearchAreas = async () => {
    try {
      const res = await api.get('/api/lead-finder/search-areas');
      setSearchAreas(res.data);
    } catch (error) {
      console.error('Failed to fetch search areas:', error);
    }
  };

  const fetchApiUsage = async () => {
    try {
      const res = await api.get('/api/lead-finder/api-usage');
      setApiUsage(res.data);
    } catch (error) {
      console.error('Failed to fetch API usage:', error);
    }
  };

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const params = {};
      if (pipelineFilter !== 'all') params.status = pipelineFilter;
      if (pipelineCategoryFilter) params.category_id = pipelineCategoryFilter;
      if (pipelineSourceFilter) params.source = pipelineSourceFilter;
      if (pipelineStateFilter) params.state = pipelineStateFilter;
      const res = await api.get('/api/lead-finder/leads', { params });
      setLeads(res.data.leads || []);
    } catch (error) {
      console.error('Failed to fetch leads:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPipelineCounts = async () => {
    try {
      const res = await api.get('/api/lead-finder/leads/counts');
      setPipelineCounts(res.data);
    } catch (error) {
      console.error('Failed to fetch pipeline counts:', error);
    }
  };

  const fetchQuotes = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/lead-finder/quotes');
      setQuotes(res.data.quotes || []);
    } catch (error) {
      console.error('Failed to fetch quotes:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProfitData = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/lead-finder/analytics/profit');
      setProfitData(res.data);
    } catch (error) {
      console.error('Failed to fetch profit data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchConversionData = async () => {
    try {
      const res = await api.get('/api/lead-finder/analytics/conversions');
      setConversionData(res.data);
    } catch (error) {
      console.error('Failed to fetch conversion data:', error);
    }
  };

  const handleSearch = async () => {
    if (!selectedCategory || !selectedArea) {
      toast.warning('Please select a category and search area');
      return;
    }

    setSearching(true);
    setSearchResults([]);
    setSelectedLeads([]);

    try {
      const res = await api.post('/api/lead-finder/search', {
        category_id: selectedCategory,
        search_area_id: selectedArea,
        providers: selectedProviders
      });

      setSearchResults(res.data.businesses || []);
      fetchApiUsage();

      if (res.data.errors?.length > 0) {
        res.data.errors.forEach(err => {
          toast.warning(`${err.provider}: ${err.error}`);
        });
      }

      toast.success(`Found ${res.data.total} new leads (${res.data.duplicatesRemoved} duplicates removed)`);
    } catch (error) {
      toast.error('Search failed');
      console.error('Search error:', error);
    } finally {
      setSearching(false);
    }
  };

  const handleImportLeads = async () => {
    if (selectedLeads.length === 0) {
      toast.warning('Please select leads to import');
      return;
    }

    try {
      const leadsToImport = searchResults.filter((_, idx) => selectedLeads.includes(idx));
      const res = await api.post('/api/lead-finder/leads/import', { businesses: leadsToImport });
      toast.success(`Imported ${res.data.imported.length} leads`);
      setSearchResults([]);
      setSelectedLeads([]);
      fetchPipelineCounts();
    } catch (error) {
      toast.error('Failed to import leads');
    }
  };

  const handleStateFilingsSearch = async () => {
    if (selectedStates.length === 0) {
      toast.warning('Please select at least one state');
      return;
    }
    setSearchingFilings(true);
    setStateFilingsResults([]);
    setSelectedFilingsLeads([]);
    try {
      const res = await api.post('/api/lead-finder/search-state-filings', {
        states: selectedStates,
        days_back: daysBack
      });
      setStateFilingsResults(res.data.businesses || []);
      toast.success(`Found ${res.data.total} new business filings`);
    } catch (error) {
      toast.error('Failed to search state filings');
      console.error('State filings search error:', error);
    } finally {
      setSearchingFilings(false);
    }
  };

  const handleImportFilingsLeads = async () => {
    if (selectedFilingsLeads.length === 0) {
      toast.warning('Please select leads to import');
      return;
    }

    try {
      const leadsToImport = stateFilingsResults.filter((_, idx) => selectedFilingsLeads.includes(idx));
      console.log('State filings to import:', leadsToImport);

      if (leadsToImport.length === 0) {
        toast.warning('No leads selected to import');
        return;
      }

      const res = await api.post('/api/lead-finder/leads/import', { businesses: leadsToImport });
      console.log('Import response:', res.data);

      if (res.data.imported.length > 0) {
        toast.success(`Imported ${res.data.imported.length} leads`);
      } else if (res.data.skipped?.length > 0) {
        toast.warning(`All ${res.data.skipped.length} leads were skipped (may already exist)`);
        console.log('Skipped leads:', res.data.skipped);
      } else {
        toast.info('No leads were imported');
      }

      setStateFilingsResults([]);
      setSelectedFilingsLeads([]);
      fetchPipelineCounts();
    } catch (error) {
      console.error('Import error:', error);
      toast.error('Failed to import leads');
    }
  };

  const formatRegistrationDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  const isHotLead = (registrationDate) => {
    if (!registrationDate) return false;
    const regDate = new Date(registrationDate);
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    return regDate >= sevenDaysAgo;
  };

  const handleUpdateLeadStatus = async (leadId, newStatus) => {
    try {
      await api.put(`/api/lead-finder/leads/${leadId}`, { lead_status: newStatus });
      toast.success('Lead status updated');
      fetchLeads();
      fetchPipelineCounts();
      if (showLeadModal) {
        fetchLeadDetail(leadId);
      }
    } catch (error) {
      toast.error('Failed to update lead');
    }
  };

  const fetchLeadDetail = async (leadId) => {
    try {
      const res = await api.get(`/api/lead-finder/leads/${leadId}`);
      setLeadDetail(res.data);
    } catch (error) {
      toast.error('Failed to fetch lead details');
    }
  };

  const openLeadModal = async (lead) => {
    setSelectedLead(lead);
    await fetchLeadDetail(lead.id);
    setShowLeadModal(true);
  };

  const handleAddActivity = async () => {
    if (!activityForm.description.trim()) {
      toast.warning('Please enter a description');
      return;
    }

    try {
      await api.post(`/api/lead-finder/leads/${leadDetail.id}/activities`, activityForm);
      toast.success('Activity added');
      setActivityForm({ activity_type: 'note_added', description: '' });
      fetchLeadDetail(leadDetail.id);
    } catch (error) {
      toast.error('Failed to add activity');
    }
  };

  const handleCreateQuote = async () => {
    try {
      const payload = {
        ...quoteForm,
        services: quoteForm.services.filter(s => s.description.trim())
      };
      await api.post(`/api/lead-finder/leads/${selectedLead.id}/quotes`, payload);
      toast.success('Quote created');
      setShowQuoteModal(false);
      setQuoteForm({
        services: [{ description: '', quantity: 1, unit_price: 0 }],
        discount_percent: 0,
        tax_percent: 0,
        valid_until: '',
        client_notes: ''
      });
      fetchQuotes();
      if (showLeadModal) {
        fetchLeadDetail(selectedLead.id);
      }
    } catch (error) {
      toast.error('Failed to create quote');
    }
  };

  const handleCreateSearchArea = async () => {
    try {
      await api.post('/api/lead-finder/search-areas', areaForm);
      toast.success('Search area created');
      setShowAreaModal(false);
      setAreaForm({ name: '', state: '', lat: '', lng: '', radius_miles: 10, county: '' });
      fetchSearchAreas();
    } catch (error) {
      toast.error('Failed to create search area');
    }
  };

  const handleDeleteSearchArea = async (areaId) => {
    if (!window.confirm('Are you sure you want to delete this search area?')) return;
    try {
      await api.delete(`/api/lead-finder/search-areas/${areaId}`);
      toast.success('Search area deleted');
      if (selectedArea === areaId) {
        setSelectedArea('');
      }
      fetchSearchAreas();
    } catch (error) {
      toast.error('Failed to delete search area');
    }
  };

  const handleDeleteLead = async (leadId) => {
    if (!window.confirm('Are you sure you want to delete this lead?')) return;
    try {
      await api.delete(`/api/lead-finder/leads/${leadId}`);
      toast.success('Lead deleted');
      setShowLeadModal(false);
      fetchLeads();
      fetchPipelineCounts();
    } catch (error) {
      toast.error('Failed to delete lead');
    }
  };

  const addServiceLine = () => {
    setQuoteForm({
      ...quoteForm,
      services: [...quoteForm.services, { description: '', quantity: 1, unit_price: 0 }]
    });
  };

  const updateServiceLine = (index, field, value) => {
    const updated = [...quoteForm.services];
    updated[index][field] = value;
    setQuoteForm({ ...quoteForm, services: updated });
  };

  const removeServiceLine = (index) => {
    const updated = quoteForm.services.filter((_, i) => i !== index);
    setQuoteForm({ ...quoteForm, services: updated });
  };

  const calculateQuoteTotal = () => {
    const subtotal = quoteForm.services.reduce((sum, s) =>
      sum + (parseFloat(s.quantity) || 0) * (parseFloat(s.unit_price) || 0), 0
    );
    const discount = subtotal * (parseFloat(quoteForm.discount_percent) || 0) / 100;
    const afterDiscount = subtotal - discount;
    const tax = afterDiscount * (parseFloat(quoteForm.tax_percent) || 0) / 100;
    return { subtotal, discount, afterDiscount, tax, total: afterDiscount + tax };
  };

  const getStatusColor = (status) => {
    const colors = {
      new: '#3b82f6',
      contacted: '#f59e0b',
      proposal: '#8b5cf6',
      won: '#10b981',
      lost: '#ef4444'
    };
    return colors[status] || '#6b7280';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  // Tab styling
  const TabButton = ({ id, label, icon: Icon, gradient, count }) => (
    <button
      onClick={() => setActiveTab(id)}
      style={{
        flex: 1,
        padding: '16px 24px',
        background: activeTab === id ? 'white' : 'transparent',
        border: 'none',
        borderBottom: activeTab === id ? '2px solid #2B7FE0' : '2px solid transparent',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
        fontSize: 14,
        fontWeight: 500,
        color: activeTab === id ? '#2B7FE0' : '#718096',
        transition: 'all 0.2s'
      }}
    >
      <div style={{
        width: 40,
        height: 40,
        borderRadius: 10,
        background: activeTab === id ? gradient : '#e2e8f0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.3s'
      }}>
        <Icon style={{ width: 22, height: 22, color: 'white' }} />
      </div>
      {label}
      {count !== undefined && (
        <span style={{
          background: activeTab === id ? '#2B7FE0' : '#e2e8f0',
          color: activeTab === id ? 'white' : '#718096',
          padding: '2px 8px',
          borderRadius: 10,
          fontSize: 12,
          fontWeight: 600
        }}>
          {count}
        </span>
      )}
    </button>
  );

  // QUOTE CALCULATOR MODAL
  const renderQuoteCalculator = () => {
    const pricing = calculateQuotePricing();
    
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1002,
        padding: 20
      }}>
        <div style={{
          background: 'white',
          borderRadius: 16,
          width: '100%',
          maxWidth: 900,
          maxHeight: '90vh',
          overflowY: 'auto'
        }}>
          {/* Header */}
          <div style={{
            padding: 24,
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            position: 'sticky',
            top: 0,
            background: 'white',
            zIndex: 1
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{
                width: 48,
                height: 48,
                borderRadius: 12,
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <CalculatorIcon style={{ width: 24, height: 24, color: 'white' }} />
              </div>
              <div>
                <h2 style={{ fontSize: 20, fontWeight: 600, margin: 0 }}>Quote Calculator</h2>
                <p style={{ fontSize: 13, color: '#718096', margin: 0 }}>Build a custom quote for {selectedLead?.name || 'your client'}</p>
              </div>
            </div>
            <button
              onClick={() => setShowCalculator(false)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 8 }}
            >
              <XMarkIcon style={{ width: 24, height: 24 }} />
            </button>
          </div>

          <div style={{ padding: 24 }}>
            {/* Business Type Presets */}
            <div style={{ marginBottom: 24 }}>
              <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: '#4a5568' }}>Quick Start - Business Type</h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {[
                  { id: 'churches', label: 'Churches' },
                  { id: 'restaurants', label: 'Restaurants' },
                  { id: 'contractors', label: 'Contractors' },
                  { id: 'salons', label: 'Salons/Spas' },
                  { id: 'medical', label: 'Medical/Dental' },
                  { id: 'legal', label: 'Legal/Accounting' },
                  { id: 'gyms', label: 'Gyms/Fitness' },
                  { id: 'auto_repair', label: 'Auto Services' },
                  { id: 'pet_services', label: 'Pet Services' },
                  { id: 'real_estate', label: 'Real Estate' }
                ].map(type => (
                  <button
                    key={type.id}
                    onClick={() => applyBusinessPreset(type.id)}
                    style={{
                      padding: '8px 16px',
                      background: calcBusinessType === type.id ? '#2B7FE0' : '#f7fafc',
                      color: calcBusinessType === type.id ? 'white' : '#4a5568',
                      border: '1px solid',
                      borderColor: calcBusinessType === type.id ? '#2B7FE0' : '#e2e8f0',
                      borderRadius: 20,
                      cursor: 'pointer',
                      fontSize: 13,
                      fontWeight: 500,
                      transition: 'all 0.2s'
                    }}
                  >
                    {type.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Pricing Tiers */}
            <div style={{ marginBottom: 24 }}>
              <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: '#4a5568' }}>Select Package</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
                {Object.entries(PRICING_TIERS).map(([key, tier]) => (
                  <div
                    key={key}
                    onClick={() => setCalcTier(key)}
                    style={{
                      padding: 20,
                      borderRadius: 12,
                      border: calcTier === key ? `3px solid ${tier.color}` : '2px solid #e2e8f0',
                      cursor: 'pointer',
                      background: calcTier === key ? `${tier.color}08` : 'white',
                      transition: 'all 0.2s'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 12 }}>
                      <h4 style={{ fontWeight: 600, color: tier.color, margin: 0 }}>{tier.name}</h4>
                      {calcTier === key && (
                        <CheckCircleIcon style={{ width: 20, height: 20, color: tier.color }} />
                      )}
                    </div>
                    <p style={{ fontSize: 12, color: '#718096', marginBottom: 12 }}>{tier.description}</p>
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 24, fontWeight: 700, color: '#1f2937' }}>
                        {formatCurrency(tier.setupFee)}
                      </div>
                      <div style={{ fontSize: 12, color: '#718096' }}>setup fee</div>
                    </div>
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 18, fontWeight: 600, color: '#1f2937' }}>
                        {formatCurrency(tier.monthlyFee)}<span style={{ fontSize: 12, fontWeight: 400 }}>/mo</span>
                      </div>
                      <div style={{ fontSize: 12, color: '#718096' }}>maintenance</div>
                    </div>
                    <div style={{ fontSize: 12, color: '#4a5568', marginBottom: 8 }}>
                      <strong>{tier.pages}</strong> • {tier.buildTime}
                    </div>
                    <div style={{ 
                      fontSize: 12, 
                      color: tier.color, 
                      fontWeight: 600, 
                      marginBottom: 8,
                      padding: '4px 8px',
                      background: `${tier.color}15`,
                      borderRadius: 4,
                      display: 'inline-block'
                    }}>
                      {tier.editsPerMonth} {typeof tier.editsPerMonth === 'number' ? 'Edits/Month' : ''}
                    </div>
                    <ul style={{ fontSize: 11, color: '#718096', margin: 0, paddingLeft: 16 }}>
                      {tier.features.slice(0, 4).map((f, i) => (
                        <li key={i} style={{ marginBottom: 2 }}>{f}</li>
                      ))}
                      {tier.features.length > 4 && (
                        <li style={{ color: tier.color }}>+{tier.features.length - 4} more</li>
                      )}
                    </ul>
                  </div>
                ))}
              </div>
            </div>

            {/* Prepaid Hosting Option */}
            <div style={{ marginBottom: 24 }}>
              <div
                onClick={() => setCalcPrepaidHosting(!calcPrepaidHosting)}
                style={{
                  padding: 20,
                  borderRadius: 12,
                  border: calcPrepaidHosting ? '3px solid #f59e0b' : '2px solid #e2e8f0',
                  cursor: 'pointer',
                  background: calcPrepaidHosting ? '#fefce8' : 'white',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  transition: 'all 0.2s'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  <div style={{
                    width: 48,
                    height: 48,
                    borderRadius: 10,
                    background: calcPrepaidHosting ? '#f59e0b' : '#e2e8f0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s'
                  }}>
                    <BoltIcon style={{ width: 24, height: 24, color: 'white' }} />
                  </div>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <h4 style={{ fontWeight: 600, margin: 0, color: calcPrepaidHosting ? '#92400e' : '#1f2937' }}>
                        Prepaid Hosting Package
                      </h4>
                      {calcPrepaidHosting && (
                        <CheckIcon style={{ width: 18, height: 18, color: '#f59e0b' }} />
                      )}
                    </div>
                    <p style={{ fontSize: 13, color: '#718096', margin: '4px 0 0 0' }}>
                      Pay {formatCurrency(PREPAID_HOSTING.upfrontCost)} upfront, save {formatCurrency(PREPAID_HOSTING.monthlyDiscount)}/mo on hosting
                    </p>
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color: '#10b981' }}>
                    Save {formatCurrency(50)}/yr
                  </div>
                  <div style={{ fontSize: 12, color: '#718096' }}>Client annual savings</div>
                </div>
              </div>
            </div>

            {/* Add-on Features */}
            <div style={{ marginBottom: 24 }}>
              <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: '#4a5568' }}>Add-on Features</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
                {ADDON_FEATURES.map(addon => (
                  <div
                    key={addon.id}
                    onClick={() => toggleAddon(addon.id)}
                    style={{
                      padding: 16,
                      borderRadius: 8,
                      border: calcAddons.includes(addon.id) ? '2px solid #10b981' : '1px solid #e2e8f0',
                      cursor: 'pointer',
                      background: calcAddons.includes(addon.id) ? '#f0fdf4' : 'white',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      transition: 'all 0.2s'
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        <span style={{ fontWeight: 500, fontSize: 14 }}>{addon.name}</span>
                        {calcAddons.includes(addon.id) && (
                          <CheckIcon style={{ width: 16, height: 16, color: '#10b981' }} />
                        )}
                      </div>
                      <p style={{ fontSize: 11, color: '#718096', margin: 0 }}>{addon.description}</p>
                    </div>
                    <div style={{ textAlign: 'right', marginLeft: 12 }}>
                      {addon.setupFee > 0 && (
                        <div style={{ fontSize: 13, fontWeight: 600 }}>{formatCurrency(addon.setupFee)}</div>
                      )}
                      {addon.monthlyFee > 0 && (
                        <div style={{ fontSize: 11, color: '#718096' }}>+{formatCurrency(addon.monthlyFee)}/mo</div>
                      )}
                      {addon.setupFee === 0 && addon.monthlyFee === 0 && (
                        <div style={{ fontSize: 13, color: '#10b981' }}>Free</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Contract & Discount */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
              <div>
                <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 8 }}>Contract Length</label>
                <select
                  value={calcContractMonths}
                  onChange={(e) => setCalcContractMonths(parseInt(e.target.value))}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
                >
                  <option value={1}>1 Month (No commitment)</option>
                  <option value={6}>6 Months</option>
                  <option value={12}>12 Months (Recommended)</option>
                  <option value={24}>24 Months</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 8 }}>Setup Discount %</label>
                <input
                  type="number"
                  value={calcDiscount}
                  onChange={(e) => setCalcDiscount(Math.min(50, Math.max(0, parseInt(e.target.value) || 0)))}
                  min="0"
                  max="50"
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
                />
              </div>
            </div>

            {/* Pricing Summary */}
            <div style={{
              background: 'linear-gradient(135deg, #1f2937 0%, #111827 100%)',
              borderRadius: 12,
              padding: 24,
              color: 'white'
            }}>
              <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16, color: '#f3f4f6' }}>Quote Summary</h3>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
                <div>
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 4 }}>Setup Fee</div>
                    <div style={{ fontSize: 28, fontWeight: 700 }}>{formatCurrency(pricing.totalSetup)}</div>
                    {pricing.discountAmount > 0 && (
                      <div style={{ fontSize: 12, color: '#10b981' }}>
                        -{formatCurrency(pricing.discountAmount)} discount = {formatCurrency(pricing.finalSetup)}
                      </div>
                    )}
                    {calcPrepaidHosting && (
                      <div style={{ fontSize: 11, color: '#fbbf24', marginTop: 4 }}>
                        Includes {formatCurrency(PREPAID_HOSTING.upfrontCost)} prepaid hosting
                      </div>
                    )}
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 4 }}>Monthly Fee</div>
                    <div style={{ fontSize: 28, fontWeight: 700 }}>
                      {formatCurrency(pricing.totalMonthly)}<span style={{ fontSize: 14, fontWeight: 400 }}>/mo</span>
                    </div>
                    {calcPrepaidHosting && (
                      <div style={{ fontSize: 11, color: '#10b981' }}>
                        -{formatCurrency(PREPAID_HOSTING.monthlyDiscount)}/mo prepaid discount applied
                      </div>
                    )}
                  </div>
                </div>
                
                <div style={{ borderLeft: '1px solid #374151', paddingLeft: 24 }}>
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 4 }}>{calcContractMonths}-Month Contract Value</div>
                    <div style={{ fontSize: 28, fontWeight: 700, color: '#10b981' }}>{formatCurrency(pricing.contractValue)}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 4 }}>Annual Recurring</div>
                    <div style={{ fontSize: 20, fontWeight: 600 }}>{formatCurrency(pricing.annualRecurring)}</div>
                  </div>
                </div>
              </div>

              <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid #374151' }}>
                <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 8 }}>Includes:</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  <span style={{ background: '#374151', padding: '4px 10px', borderRadius: 4, fontSize: 12 }}>
                    {pricing.tier.name} Package
                  </span>
                  <span style={{ background: '#374151', padding: '4px 10px', borderRadius: 4, fontSize: 12 }}>
                    {pricing.tier.editsPerMonth} {typeof pricing.tier.editsPerMonth === 'number' ? 'Edits/Mo' : ''}
                  </span>
                  {calcPrepaidHosting && (
                    <span style={{ background: '#92400e', padding: '4px 10px', borderRadius: 4, fontSize: 12 }}>
                      Prepaid Hosting
                    </span>
                  )}
                  {pricing.selectedAddons.map(addon => (
                    <span key={addon.id} style={{ background: '#374151', padding: '4px 10px', borderRadius: 4, fontSize: 12 }}>
                      {addon.name}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div style={{
            padding: 16,
            borderTop: '1px solid #e2e8f0',
            display: 'flex',
            justifyContent: 'space-between',
            background: '#f9fafb'
          }}>
            <button
              onClick={() => setShowCalculator(false)}
              style={{
                padding: '12px 24px',
                background: '#e2e8f0',
                color: '#1f2937',
                border: 'none',
                borderRadius: 8,
                cursor: 'pointer',
                fontSize: 14
              }}
            >
              Cancel
            </button>
            <button
              onClick={generateQuoteFromCalculator}
              style={{
                padding: '12px 32px',
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: 'white',
                border: 'none',
                borderRadius: 8,
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 8
              }}
            >
              Generate Quote
              <ArrowRightIcon style={{ width: 16, height: 16 }} />
            </button>
          </div>
        </div>
      </div>
    );
  };

  // SEARCH TAB
  const renderSearchTab = () => (
    <div className="card-body">
      {/* API Usage Status */}
      <div style={{ marginBottom: 24 }}>
        <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: '#4a5568' }}>API Budget Status</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
          {apiUsage.filter(u => ['google', 'foursquare'].includes(u.provider)).map(usage => (
            <div key={usage.provider} style={{
              padding: 12,
              background: '#f7fafc',
              borderRadius: 8,
              border: '1px solid #e2e8f0'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontWeight: 600, textTransform: 'capitalize' }}>{usage.provider}</span>
                {usage.monthly_percent_used > 80 && (
                  <ExclamationTriangleIcon style={{ width: 16, height: 16, color: '#f59e0b' }} />
                )}
              </div>
              <div style={{ fontSize: 12, color: '#718096' }}>
                {usage.daily_limit ? (
                  <div>Today: {usage.calls_today}/{usage.daily_limit} calls</div>
                ) : (
                  <div>Spend: ${parseFloat(usage.spend_this_month || 0).toFixed(2)}/${usage.monthly_spend_limit}</div>
                )}
              </div>
              <div style={{
                height: 4,
                background: '#e2e8f0',
                borderRadius: 2,
                marginTop: 8,
                overflow: 'hidden'
              }}>
                <div style={{
                  height: '100%',
                  width: `${Math.min(usage.daily_percent_used || usage.monthly_percent_used || 0, 100)}%`,
                  background: (usage.daily_percent_used || usage.monthly_percent_used || 0) > 80
                    ? '#f59e0b'
                    : '#10b981',
                  borderRadius: 2
                }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Search Form */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 16, marginBottom: 24 }}>
        <div>
          <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 8 }}>Category</label>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
          >
            <option value="">Select category...</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.display_name}</option>
            ))}
          </select>
        </div>

        <div>
          <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 8 }}>Search Area</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <select
              value={selectedArea}
              onChange={(e) => setSelectedArea(e.target.value)}
              style={{ flex: 1, padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
            >
              <option value="">Select area...</option>
              {searchAreas.map(area => (
                <option key={area.id} value={area.id}>{area.name}, {area.state}</option>
              ))}
            </select>
            {isAdmin && (
              <>
                <button
                  onClick={() => setShowAreaModal(true)}
                  style={{
                    padding: '10px 12px',
                    background: '#e2e8f0',
                    border: 'none',
                    borderRadius: 6,
                    cursor: 'pointer'
                  }}
                  title="Add Search Area"
                >
                  <PlusIcon style={{ width: 18, height: 18 }} />
                </button>
                {selectedArea && (
                  <button
                    onClick={() => handleDeleteSearchArea(selectedArea)}
                    style={{
                      padding: '10px 12px',
                      background: '#fee2e2',
                      border: 'none',
                      borderRadius: 6,
                      cursor: 'pointer'
                    }}
                    title="Delete Selected Area"
                  >
                    <TrashIcon style={{ width: 18, height: 18, color: '#991b1b' }} />
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'flex-end' }}>
          <button
            onClick={handleSearch}
            disabled={searching}
            style={{
              padding: '10px 24px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              borderRadius: 6,
              cursor: searching ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 14,
              fontWeight: 500,
              opacity: searching ? 0.7 : 1
            }}
          >
            {searching ? (
              <ArrowPathIcon style={{ width: 18, height: 18, animation: 'spin 1s linear infinite' }} />
            ) : (
              <MagnifyingGlassIcon style={{ width: 18, height: 18 }} />
            )}
            {searching ? 'Searching...' : 'Search'}
          </button>
        </div>
      </div>

      {/* Provider Selection */}
      <div style={{ marginBottom: 24 }}>
        <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 8 }}>Search Providers</label>
        <div style={{ display: 'flex', gap: 12 }}>
          {['google', 'foursquare'].map(provider => (
            <label key={provider} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={selectedProviders.includes(provider)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedProviders([...selectedProviders, provider]);
                  } else {
                    setSelectedProviders(selectedProviders.filter(p => p !== provider));
                  }
                }}
              />
              <span style={{ textTransform: 'capitalize' }}>{provider === 'google' ? 'Google Places' : 'Foursquare'}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Search Results */}
      {searchResults.length > 0 && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ fontSize: 16, fontWeight: 600 }}>
              Search Results ({searchResults.length})
            </h3>
            <div style={{ display: 'flex', gap: 12 }}>
              <button
                onClick={() => {
                  if (selectedLeads.length === searchResults.length) {
                    setSelectedLeads([]);
                  } else {
                    setSelectedLeads(searchResults.map((_, i) => i));
                  }
                }}
                style={{
                  padding: '8px 16px',
                  background: '#f7fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontSize: 13
                }}
              >
                {selectedLeads.length === searchResults.length ? 'Deselect All' : 'Select All'}
              </button>
              <button
                onClick={handleImportLeads}
                disabled={selectedLeads.length === 0}
                style={{
                  padding: '8px 16px',
                  background: selectedLeads.length > 0 ? '#10b981' : '#e2e8f0',
                  color: selectedLeads.length > 0 ? 'white' : '#718096',
                  border: 'none',
                  borderRadius: 6,
                  cursor: selectedLeads.length > 0 ? 'pointer' : 'not-allowed',
                  fontSize: 13,
                  fontWeight: 500
                }}
              >
                Import Selected ({selectedLeads.length})
              </button>
            </div>
          </div>

          <div style={{ display: 'grid', gap: 12 }}>
            {searchResults.map((business, index) => (
              <div
                key={index}
                onClick={() => {
                  if (selectedLeads.includes(index)) {
                    setSelectedLeads(selectedLeads.filter(i => i !== index));
                  } else {
                    setSelectedLeads([...selectedLeads, index]);
                  }
                }}
                style={{
                  padding: 16,
                  background: selectedLeads.includes(index) ? '#eff6ff' : '#f7fafc',
                  borderRadius: 8,
                  border: selectedLeads.includes(index) ? '2px solid #3b82f6' : '1px solid #e2e8f0',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                      <h4 style={{ fontWeight: 600, fontSize: 15 }}>{business.name}</h4>
                      <span style={{
                        padding: '2px 8px',
                        background: business.has_website ? '#d1fae5' : '#fee2e2',
                        color: business.has_website ? '#065f46' : '#991b1b',
                        borderRadius: 4,
                        fontSize: 11,
                        fontWeight: 500
                      }}>
                        {business.has_website ? 'Has Website' : 'No Website'}
                      </span>
                      <span style={{
                        padding: '2px 8px',
                        background: '#e0e7ff',
                        color: '#3730a3',
                        borderRadius: 4,
                        fontSize: 11,
                        textTransform: 'capitalize'
                      }}>
                        {business.source}
                      </span>
                    </div>

                    <div style={{ display: 'flex', gap: 16, fontSize: 13, color: '#4a5568' }}>
                      {business.address && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <MapPinIcon style={{ width: 14, height: 14 }} />
                          {business.address}
                        </span>
                      )}
                      {business.phone && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <PhoneIcon style={{ width: 14, height: 14 }} />
                          {business.phone}
                        </span>
                      )}
                    </div>

                    {(business.google_rating || business.foursquare_rating) && (
                      <div style={{ display: 'flex', gap: 12, marginTop: 8, fontSize: 12 }}>
                        {business.google_rating && (
                          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <StarIcon style={{ width: 14, height: 14, color: '#fbbf24' }} />
                            {business.google_rating} ({business.google_review_count} reviews)
                          </span>
                        )}
                        {business.foursquare_rating && (
                          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <StarIcon style={{ width: 14, height: 14, color: '#8b5cf6' }} />
                            {business.foursquare_rating}
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  <div style={{ textAlign: 'right' }}>
                    <div style={{
                      fontSize: 24,
                      fontWeight: 700,
                      color: business.lead_score >= 70 ? '#10b981' : business.lead_score >= 50 ? '#f59e0b' : '#6b7280'
                    }}>
                      {business.lead_score}
                    </div>
                    <div style={{ fontSize: 11, color: '#718096' }}>Lead Score</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {searchResults.length === 0 && !searching && stateFilingsResults.length === 0 && !searchingFilings && (
        <div style={{ padding: 60, textAlign: 'center', color: '#718096' }}>
          <MagnifyingGlassIcon style={{ width: 48, height: 48, margin: '0 auto 16px', opacity: 0.5 }} />
          <p>Select a category and search area, then click Search to find leads</p>
        </div>
      )}

      {/* State Business Filings Section */}
      <div style={{
        marginTop: 32,
        padding: 24,
        background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
        borderRadius: 12,
        border: '1px solid #86efac'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <div style={{
            width: 40,
            height: 40,
            borderRadius: 10,
            background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <DocumentTextIcon style={{ width: 20, height: 20, color: 'white' }} />
          </div>
          <div>
            <h3 style={{ fontSize: 16, fontWeight: 600, color: '#166534', margin: 0 }}>
              State Business Filings (FREE)
            </h3>
            <p style={{ fontSize: 13, color: '#15803d', margin: 0 }}>
              Find newly registered businesses from official state databases
            </p>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 16, alignItems: 'end' }}>
          {/* State Checkboxes */}
          <div>
            <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 8, color: '#166534' }}>
              Select States
            </label>
            <div style={{ display: 'flex', gap: 16 }}>
              {['NY', 'CT', 'OR'].map(state => (
                <label key={state} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={selectedStates.includes(state)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedStates([...selectedStates, state]);
                      } else {
                        setSelectedStates(selectedStates.filter(s => s !== state));
                      }
                    }}
                    style={{ accentColor: '#22c55e' }}
                  />
                  <span style={{ fontWeight: 500, color: '#166534' }}>
                    {state === 'NY' ? 'New York' : state === 'CT' ? 'Connecticut' : 'Oregon'}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Days Back Dropdown */}
          <div>
            <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 8, color: '#166534' }}>
              Registered Within
            </label>
            <select
              value={daysBack}
              onChange={(e) => setDaysBack(Number(e.target.value))}
              style={{
                padding: '10px 12px',
                border: '1px solid #86efac',
                borderRadius: 6,
                fontSize: 14,
                background: 'white',
                minWidth: 140
              }}
            >
              <option value={7}>Last 7 days</option>
              <option value={14}>Last 14 days</option>
              <option value={30}>Last 30 days</option>
              <option value={60}>Last 60 days</option>
              <option value={90}>Last 90 days</option>
            </select>
          </div>

          {/* Search Button */}
          <button
            onClick={handleStateFilingsSearch}
            disabled={searchingFilings}
            style={{
              padding: '10px 24px',
              background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
              color: 'white',
              border: 'none',
              borderRadius: 6,
              cursor: searchingFilings ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 14,
              fontWeight: 500,
              opacity: searchingFilings ? 0.7 : 1
            }}
          >
            {searchingFilings ? (
              <ArrowPathIcon style={{ width: 18, height: 18, animation: 'spin 1s linear infinite' }} />
            ) : (
              <MagnifyingGlassIcon style={{ width: 18, height: 18 }} />
            )}
            {searchingFilings ? 'Searching...' : 'Search State Filings'}
          </button>
        </div>
      </div>

      {/* State Filings Results */}
      {stateFilingsResults.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, color: '#166534' }}>
              State Filings Results ({stateFilingsResults.length})
            </h3>
            <div style={{ display: 'flex', gap: 12 }}>
              <button
                onClick={() => {
                  if (selectedFilingsLeads.length === stateFilingsResults.length) {
                    setSelectedFilingsLeads([]);
                  } else {
                    setSelectedFilingsLeads(stateFilingsResults.map((_, i) => i));
                  }
                }}
                style={{
                  padding: '8px 16px',
                  background: '#f7fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontSize: 13
                }}
              >
                {selectedFilingsLeads.length === stateFilingsResults.length ? 'Deselect All' : 'Select All'}
              </button>
              <button
                onClick={handleImportFilingsLeads}
                disabled={selectedFilingsLeads.length === 0}
                style={{
                  padding: '8px 16px',
                  background: selectedFilingsLeads.length > 0 ? 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)' : '#e2e8f0',
                  color: selectedFilingsLeads.length > 0 ? 'white' : '#718096',
                  border: 'none',
                  borderRadius: 6,
                  cursor: selectedFilingsLeads.length > 0 ? 'pointer' : 'not-allowed',
                  fontSize: 13,
                  fontWeight: 500
                }}
              >
                Import Selected ({selectedFilingsLeads.length})
              </button>
            </div>
          </div>

          <div style={{ display: 'grid', gap: 12 }}>
            {stateFilingsResults.map((business, index) => (
              <div
                key={index}
                onClick={() => {
                  if (selectedFilingsLeads.includes(index)) {
                    setSelectedFilingsLeads(selectedFilingsLeads.filter(i => i !== index));
                  } else {
                    setSelectedFilingsLeads([...selectedFilingsLeads, index]);
                  }
                }}
                style={{
                  padding: 16,
                  background: selectedFilingsLeads.includes(index) ? '#f0fdf4' : '#f7fafc',
                  borderRadius: 8,
                  border: selectedFilingsLeads.includes(index) ? '2px solid #22c55e' : '1px solid #e2e8f0',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                      <h4 style={{ fontWeight: 600, fontSize: 15 }}>{business.name}</h4>
                      {isHotLead(business.registration_date) && (
                        <span style={{
                          padding: '2px 8px',
                          background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
                          color: 'white',
                          borderRadius: 4,
                          fontSize: 11,
                          fontWeight: 600,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 4
                        }}>
                          🔥 Hot Lead
                        </span>
                      )}
                      <span style={{
                        padding: '2px 8px',
                        background: '#dcfce7',
                        color: '#166534',
                        borderRadius: 4,
                        fontSize: 11,
                        fontWeight: 500
                      }}>
                        {business.state}
                      </span>
                      <span style={{
                        padding: '2px 8px',
                        background: '#e0f2fe',
                        color: '#0369a1',
                        borderRadius: 4,
                        fontSize: 11
                      }}>
                        {business.business_type || 'Business'}
                      </span>
                    </div>

                    <div style={{ display: 'flex', gap: 16, fontSize: 13, color: '#4a5568' }}>
                      {business.city && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <MapPinIcon style={{ width: 14, height: 14 }} />
                          {business.city}, {business.state}
                        </span>
                      )}
                      {business.registration_date && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <DocumentTextIcon style={{ width: 14, height: 14 }} />
                          Registered: {formatRegistrationDate(business.registration_date)}
                        </span>
                      )}
                    </div>
                  </div>

                  <div style={{ textAlign: 'right' }}>
                    <div style={{
                      fontSize: 24,
                      fontWeight: 700,
                      color: business.lead_score >= 70 ? '#16a34a' : business.lead_score >= 50 ? '#ca8a04' : '#6b7280'
                    }}>
                      {business.lead_score || '-'}
                    </div>
                    <div style={{ fontSize: 11, color: '#718096' }}>Lead Score</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  // PIPELINE TAB
  const renderPipelineTab = () => {
    const pipelineStages = [
      { id: 'new', label: 'New', color: '#3b82f6' },
      { id: 'contacted', label: 'Contacted', color: '#f59e0b' },
      { id: 'proposal', label: 'Proposal', color: '#8b5cf6' },
      { id: 'won', label: 'Won', color: '#10b981' },
      { id: 'lost', label: 'Lost', color: '#ef4444' }
    ];

    return (
      <div className="card-body">
        {/* Filters */}
        <div style={{ display: 'flex', gap: 16, marginBottom: 20 }}>
          <div>
            <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 8 }}>Filter by Category</label>
            <select
              value={pipelineCategoryFilter}
              onChange={(e) => setPipelineCategoryFilter(e.target.value)}
              style={{ padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, minWidth: 200 }}
            >
              <option value="">All Categories</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.display_name}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 8 }}>Filter by Source</label>
            <select
              value={pipelineSourceFilter}
              onChange={(e) => setPipelineSourceFilter(e.target.value)}
              style={{ padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, minWidth: 200 }}
            >
              <option value="">All Sources</option>
              <option value="google">Google Places</option>
              <option value="state_filings">State Filings</option>
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 8 }}>Filter by State</label>
            <select
              value={pipelineStateFilter}
              onChange={(e) => setPipelineStateFilter(e.target.value)}
              style={{ padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, minWidth: 200 }}
            >
              <option value="">All States</option>
              <option value="AZ">Arizona</option>
              <option value="CT">Connecticut</option>
              <option value="FL">Florida</option>
              <option value="NJ">New Jersey</option>
              <option value="NY">New York</option>
              <option value="OR">Oregon</option>
              <option value="PA">Pennsylvania</option>
            </select>
          </div>
        </div>

        {/* Pipeline Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 24 }}>
          {pipelineStages.map(stage => (
            <div
              key={stage.id}
              onClick={() => setPipelineFilter(pipelineFilter === stage.id ? 'all' : stage.id)}
              style={{
                padding: 16,
                background: pipelineFilter === stage.id ? stage.color : '#f7fafc',
                borderRadius: 8,
                textAlign: 'center',
                cursor: 'pointer',
                border: `2px solid ${pipelineFilter === stage.id ? stage.color : '#e2e8f0'}`,
                transition: 'all 0.2s'
              }}
            >
              <div style={{
                fontSize: 28,
                fontWeight: 700,
                color: pipelineFilter === stage.id ? 'white' : stage.color
              }}>
                {pipelineCounts[stage.id] || 0}
              </div>
              <div style={{
                fontSize: 13,
                color: pipelineFilter === stage.id ? 'rgba(255,255,255,0.9)' : '#4a5568'
              }}>
                {stage.label}
              </div>
            </div>
          ))}
        </div>

        {/* Leads List */}
        <div style={{ display: 'grid', gap: 12 }}>
          {leads.map(lead => (
            <div
              key={lead.id}
              style={{
                padding: 16,
                background: 'white',
                borderRadius: 8,
                border: '1px solid #e2e8f0',
                display: 'flex',
                alignItems: 'center',
                gap: 16
              }}
            >
              <div style={{
                width: 8,
                height: 60,
                borderRadius: 4,
                background: getStatusColor(lead.lead_status)
              }} />

              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
                  <h4 style={{ fontWeight: 600 }}>{lead.name}</h4>
                  <span style={{
                    padding: '2px 8px',
                    background: `${getStatusColor(lead.lead_status)}20`,
                    color: getStatusColor(lead.lead_status),
                    borderRadius: 4,
                    fontSize: 11,
                    fontWeight: 500,
                    textTransform: 'capitalize'
                  }}>
                    {lead.lead_status}
                  </span>
                  {lead.category_name && (
                    <span style={{ fontSize: 12, color: '#718096' }}>{lead.category_name}</span>
                  )}
                </div>

                <div style={{ display: 'flex', gap: 16, fontSize: 13, color: '#4a5568' }}>
                  {lead.phone && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <PhoneIcon style={{ width: 14, height: 14 }} />
                      {lead.phone}
                    </span>
                  )}
                  {lead.city && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <MapPinIcon style={{ width: 14, height: 14 }} />
                      {lead.city}, {lead.state}
                    </span>
                  )}
                  {!lead.has_website && (
                    <span style={{ color: '#10b981', fontWeight: 500 }}>No Website</span>
                  )}
                </div>
              </div>

              <div style={{ textAlign: 'right', marginRight: 16 }}>
                <div style={{ fontSize: 20, fontWeight: 700, color: '#4a5568' }}>{lead.lead_score}</div>
                <div style={{ fontSize: 11, color: '#a0aec0' }}>Score</div>
              </div>

              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={() => openLeadModal(lead)}
                  style={{
                    padding: '8px 12px',
                    background: '#2B7FE0',
                    color: 'white',
                    border: 'none',
                    borderRadius: 6,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    fontSize: 13
                  }}
                >
                  <EyeIcon style={{ width: 16, height: 16 }} />
                  View
                </button>

                {lead.lead_status === 'new' && (
                  <button
                    onClick={() => { setContactingLead(lead); setShowContactModal(true); }}
                    style={{
                      padding: '8px 12px',
                      background: '#f59e0b',
                      color: 'white',
                      border: 'none',
                      borderRadius: 6,
                      cursor: 'pointer',
                      fontSize: 13
                    }}
                  >
                    Mark Contacted
                  </button>
                )}

                {lead.lead_status === 'contacted' && (
                  <button
                    onClick={() => {
                      setSelectedLead(lead);
                      setShowCalculator(true);
                    }}
                    style={{
                      padding: '8px 12px',
                      background: '#8b5cf6',
                      color: 'white',
                      border: 'none',
                      borderRadius: 6,
                      cursor: 'pointer',
                      fontSize: 13
                    }}
                  >
                    Create Quote
                  </button>
                )}
              </div>
            </div>
          ))}

          {leads.length === 0 && (
            <div style={{ padding: 60, textAlign: 'center', color: '#718096' }}>
              <FunnelIcon style={{ width: 48, height: 48, margin: '0 auto 16px', opacity: 0.5 }} />
              <p>No leads found. Use the Search tab to find new leads.</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  // QUOTES TAB - WITH CALCULATOR BUTTON
  const renderQuotesTab = () => (
    <div className="card-body">
      {/* Quote Calculator Button */}
      <div style={{
        padding: 20,
        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
        borderRadius: 12,
        marginBottom: 24,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        color: 'white'
      }}>
        <div>
          <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 4 }}>Quote Calculator</h3>
          <p style={{ fontSize: 14, opacity: 0.9, margin: 0 }}>Build custom quotes with pricing tiers and add-ons</p>
        </div>
        <button
          onClick={() => {
            if (leads.length > 0) {
              setSelectedLead(leads[0]);
            }
            setShowCalculator(true);
          }}
          style={{
            padding: '12px 24px',
            background: 'white',
            color: '#059669',
            border: 'none',
            borderRadius: 8,
            cursor: 'pointer',
            fontSize: 14,
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: 8
          }}
        >
          <CalculatorIcon style={{ width: 18, height: 18 }} />
          Open Calculator
        </button>
      </div>

      {/* Pricing Tiers Summary */}
      <div style={{ marginBottom: 24 }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>Your Pricing Tiers</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
          {Object.entries(PRICING_TIERS).map(([key, tier]) => (
            <div key={key} style={{
              padding: 16,
              background: '#f7fafc',
              borderRadius: 8,
              borderTop: `4px solid ${tier.color}`
            }}>
              <h4 style={{ fontWeight: 600, color: tier.color, marginBottom: 8 }}>{tier.name}</h4>
              <div style={{ fontSize: 24, fontWeight: 700, marginBottom: 4 }}>{formatCurrency(tier.setupFee)}</div>
              <div style={{ fontSize: 13, color: '#718096' }}>+ {formatCurrency(tier.monthlyFee)}/mo</div>
              <div style={{ fontSize: 12, color: '#4a5568', marginTop: 8 }}>{tier.pages}</div>
              <div style={{ 
                fontSize: 11, 
                color: tier.color, 
                fontWeight: 600, 
                marginTop: 4,
                padding: '2px 6px',
                background: `${tier.color}15`,
                borderRadius: 4,
                display: 'inline-block'
              }}>
                {tier.editsPerMonth} {typeof tier.editsPerMonth === 'number' ? 'edits/mo' : ''}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Existing Quotes List */}
      <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>Recent Quotes</h3>
      <div style={{ display: 'grid', gap: 12 }}>
        {quotes.map(quote => (
          <div
            key={quote.id}
            style={{
              padding: 16,
              background: 'white',
              borderRadius: 8,
              border: '1px solid #e2e8f0'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                  <h4 style={{ fontWeight: 600 }}>{quote.quote_number}</h4>
                  <span style={{
                    padding: '2px 8px',
                    background: quote.status === 'accepted' ? '#d1fae5' :
                              quote.status === 'rejected' ? '#fee2e2' :
                              quote.status === 'sent' ? '#dbeafe' : '#f3f4f6',
                    color: quote.status === 'accepted' ? '#065f46' :
                           quote.status === 'rejected' ? '#991b1b' :
                           quote.status === 'sent' ? '#1e40af' : '#4b5563',
                    borderRadius: 4,
                    fontSize: 11,
                    fontWeight: 500,
                    textTransform: 'capitalize'
                  }}>
                    {quote.status}
                  </span>
                </div>
                <div style={{ fontSize: 14, color: '#4a5568', marginBottom: 4 }}>
                  {quote.lead_name}
                </div>
                <div style={{ fontSize: 12, color: '#718096' }}>
                  Created {formatDate(quote.created_at)}
                  {quote.valid_until && ` | Valid until ${formatDate(quote.valid_until)}`}
                </div>
              </div>

              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 24, fontWeight: 700, color: '#10b981' }}>
                  {formatCurrency(quote.total)}
                </div>
                <div style={{ fontSize: 12, color: '#718096' }}>
                  {(quote.services || []).length} line items
                </div>
              </div>
            </div>

            <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #e2e8f0' }}>
              <div style={{ display: 'flex', gap: 8 }}>
                {quote.status === 'draft' && (
                  <button
                    onClick={async () => {
                      try {
                        await api.put(`/api/lead-finder/quotes/${quote.id}`, { status: 'sent' });
                        toast.success('Quote marked as sent');
                        fetchQuotes();
                      } catch (error) {
                        toast.error('Failed to update quote');
                      }
                    }}
                    style={{
                      padding: '6px 12px',
                      background: '#3b82f6',
                      color: 'white',
                      border: 'none',
                      borderRadius: 4,
                      cursor: 'pointer',
                      fontSize: 12
                    }}
                  >
                    Mark Sent
                  </button>
                )}
                {quote.status === 'sent' && (
                  <>
                    <button
                      onClick={async () => {
                        try {
                          await api.put(`/api/lead-finder/quotes/${quote.id}`, { status: 'accepted' });
                          await api.put(`/api/lead-finder/leads/${quote.lead_id}`, { lead_status: 'won' });
                          toast.success('Quote accepted - Lead marked as Won!');
                          fetchQuotes();
                          fetchPipelineCounts();
                        } catch (error) {
                          toast.error('Failed to update');
                        }
                      }}
                      style={{
                        padding: '6px 12px',
                        background: '#10b981',
                        color: 'white',
                        border: 'none',
                        borderRadius: 4,
                        cursor: 'pointer',
                        fontSize: 12
                      }}
                    >
                      Accepted
                    </button>
                    <button
                      onClick={async () => {
                        try {
                          await api.put(`/api/lead-finder/quotes/${quote.id}`, { status: 'rejected' });
                          await api.put(`/api/lead-finder/leads/${quote.lead_id}`, { lead_status: 'lost' });
                          toast.success('Quote rejected - Lead marked as Lost');
                          fetchQuotes();
                          fetchPipelineCounts();
                        } catch (error) {
                          toast.error('Failed to update');
                        }
                      }}
                      style={{
                        padding: '6px 12px',
                        background: '#ef4444',
                        color: 'white',
                        border: 'none',
                        borderRadius: 4,
                        cursor: 'pointer',
                        fontSize: 12
                      }}
                    >
                      Rejected
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        ))}

        {quotes.length === 0 && (
          <div style={{ padding: 60, textAlign: 'center', color: '#718096' }}>
            <DocumentTextIcon style={{ width: 48, height: 48, margin: '0 auto 16px', opacity: 0.5 }} />
            <p>No quotes yet. Use the Quote Calculator to create your first quote!</p>
          </div>
        )}
      </div>
    </div>
  );

  // PROFIT TAB
  const renderProfitTab = () => {
    const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444', '#6366f1'];

    if (!profitData) {
      return (
        <div className="card-body" style={{ padding: 60, textAlign: 'center', color: '#718096' }}>
          <ChartBarIcon style={{ width: 48, height: 48, margin: '0 auto 16px', opacity: 0.5 }} />
          <p>Loading analytics...</p>
        </div>
      );
    }

    return (
      <div className="card-body">
        {/* Summary Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 32 }}>
          <div style={{
            padding: 20,
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            borderRadius: 12,
            color: 'white'
          }}>
            <div style={{ fontSize: 14, opacity: 0.9, marginBottom: 4 }}>Total Revenue</div>
            <div style={{ fontSize: 32, fontWeight: 700 }}>
              {formatCurrency(profitData.summary?.total_revenue)}
            </div>
          </div>
          <div style={{
            padding: 20,
            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
            borderRadius: 12,
            color: 'white'
          }}>
            <div style={{ fontSize: 14, opacity: 0.9, marginBottom: 4 }}>Total Costs</div>
            <div style={{ fontSize: 32, fontWeight: 700 }}>
              {formatCurrency(profitData.summary?.total_costs)}
            </div>
          </div>
          <div style={{
            padding: 20,
            background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
            borderRadius: 12,
            color: 'white'
          }}>
            <div style={{ fontSize: 14, opacity: 0.9, marginBottom: 4 }}>Net Profit</div>
            <div style={{ fontSize: 32, fontWeight: 700 }}>
              {formatCurrency(profitData.summary?.net_profit)}
            </div>
          </div>
        </div>

        {/* Charts */}
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 24, marginBottom: 32 }}>
          {/* Monthly Trend */}
          <div style={{ background: 'white', padding: 20, borderRadius: 8, border: '1px solid #e2e8f0' }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>Monthly Trend</h3>
            {profitData.monthlyTrend?.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={profitData.monthlyTrend}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Legend />
                  <Line type="monotone" dataKey="revenue" stroke="#10b981" name="Revenue" strokeWidth={2} />
                  <Line type="monotone" dataKey="costs" stroke="#ef4444" name="Costs" strokeWidth={2} />
                  <Line type="monotone" dataKey="profit" stroke="#3b82f6" name="Profit" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ height: 250, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#718096' }}>
                No data yet
              </div>
            )}
          </div>

          {/* By Category */}
          <div style={{ background: 'white', padding: 20, borderRadius: 8, border: '1px solid #e2e8f0' }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>By Category</h3>
            {profitData.byCategory?.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={profitData.byCategory}
                    dataKey="profit"
                    nameKey="category"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label={({ category, percent }) => `${category} ${(percent * 100).toFixed(0)}%`}
                  >
                    {profitData.byCategory.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ height: 250, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#718096' }}>
                No data yet
              </div>
            )}
          </div>
        </div>

        {/* Conversion Stats */}
        {conversionData && (
          <div style={{ background: 'white', padding: 20, borderRadius: 8, border: '1px solid #e2e8f0' }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>Conversion Rates</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 16 }}>
              <div style={{ textAlign: 'center', padding: 16, background: '#f7fafc', borderRadius: 8 }}>
                <div style={{ fontSize: 28, fontWeight: 700, color: '#3b82f6' }}>{conversionData.overall?.total_leads || 0}</div>
                <div style={{ fontSize: 12, color: '#718096' }}>Total Leads</div>
              </div>
              <div style={{ textAlign: 'center', padding: 16, background: '#f7fafc', borderRadius: 8 }}>
                <div style={{ fontSize: 28, fontWeight: 700, color: '#10b981' }}>{conversionData.overall?.won || 0}</div>
                <div style={{ fontSize: 12, color: '#718096' }}>Won</div>
              </div>
              <div style={{ textAlign: 'center', padding: 16, background: '#f7fafc', borderRadius: 8 }}>
                <div style={{ fontSize: 28, fontWeight: 700, color: '#ef4444' }}>{conversionData.overall?.lost || 0}</div>
                <div style={{ fontSize: 12, color: '#718096' }}>Lost</div>
              </div>
              <div style={{ textAlign: 'center', padding: 16, background: '#f7fafc', borderRadius: 8 }}>
                <div style={{ fontSize: 28, fontWeight: 700, color: '#8b5cf6' }}>{conversionData.overall?.win_rate || 0}%</div>
                <div style={{ fontSize: 12, color: '#718096' }}>Win Rate</div>
              </div>
            </div>

            {conversionData.byCategory?.length > 0 && (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={conversionData.byCategory}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="category" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="won" fill="#10b981" name="Won" />
                  <Bar dataKey="lost" fill="#ef4444" name="Lost" />
                  <Bar dataKey="active" fill="#3b82f6" name="Active" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="dashboard">
      <div className="page-header" style={{ marginBottom: 24 }}>
        <h1 className="page-title" style={{ fontSize: 28, fontWeight: 600, marginBottom: 8 }}>Lead Finder</h1>
        <p style={{ color: '#718096', fontSize: 14 }}>Find businesses without websites and track them through your sales pipeline</p>
      </div>

      <div className="card">
        <div style={{ borderBottom: '1px solid #e2e8f0' }}>
          <div style={{ display: 'flex', gap: 0 }}>
            <TabButton
              id="search"
              label="Search"
              icon={MagnifyingGlassIcon}
              gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
            />
            <TabButton
              id="pipeline"
              label="Pipeline"
              icon={FunnelIcon}
              gradient="linear-gradient(135deg, #f59e0b 0%, #d97706 100%)"
              count={pipelineCounts.total || 0}
            />
            <TabButton
              id="quotes"
              label="Quotes"
              icon={DocumentTextIcon}
              gradient="linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)"
            />
            {isAdmin && (
              <TabButton
                id="invoices"
                label="Invoices"
                icon={BanknotesIcon}
                gradient="linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)"
              />
            )}
            {isAdmin && (
              <TabButton
                id="profit"
                label="Profit"
                icon={CurrencyDollarIcon}
                gradient="linear-gradient(135deg, #10b981 0%, #059669 100%)"
              />
            )}
          </div>
        </div>

        {activeTab === 'search' && renderSearchTab()}
        {activeTab === 'pipeline' && renderPipelineTab()}
        {activeTab === 'quotes' && renderQuotesTab()}
        {activeTab === 'invoices' && <WebInvoicesTab />}
        {activeTab === 'profit' && renderProfitTab()}
      </div>

      {/* Quote Calculator Modal */}
      {showCalculator && renderQuoteCalculator()}

      {/* Lead Detail Modal */}
      {showLeadModal && leadDetail && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: 20
        }}>
          <div style={{
            background: 'white',
            borderRadius: 12,
            width: '100%',
            maxWidth: 800,
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <div style={{
              padding: 24,
              borderBottom: '1px solid #e2e8f0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'start',
              position: 'sticky',
              top: 0,
              background: 'white',
              zIndex: 1
            }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                  <div style={{
                    width: 48,
                    height: 48,
                    borderRadius: 10,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <BuildingStorefrontIcon style={{ width: 24, height: 24, color: 'white' }} />
                  </div>
                  <div>
                    <h2 style={{ fontSize: 20, fontWeight: 600 }}>{leadDetail.name}</h2>
                    <span style={{
                      padding: '4px 12px',
                      background: `${getStatusColor(leadDetail.lead_status)}20`,
                      color: getStatusColor(leadDetail.lead_status),
                      borderRadius: 4,
                      fontSize: 12,
                      fontWeight: 500,
                      textTransform: 'capitalize'
                    }}>
                      {leadDetail.lead_status}
                    </span>
                  </div>
                </div>
              </div>
              <button
                onClick={() => setShowLeadModal(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
              >
                <XMarkIcon style={{ width: 24, height: 24 }} />
              </button>
            </div>

            <div style={{ padding: 24 }}>
              {/* Lead Info */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 24 }}>
                <div>
                  <h3 style={{ fontSize: 14, fontWeight: 600, color: '#4a5568', marginBottom: 12 }}>Contact Info</h3>
                  <div style={{ display: 'grid', gap: 8 }}>
                    {leadDetail.phone && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <PhoneIcon style={{ width: 16, height: 16, color: '#718096' }} />
                        <a href={`tel:${leadDetail.phone}`} style={{ color: '#2B7FE0' }}>{leadDetail.phone}</a>
                      </div>
                    )}
                    {leadDetail.address && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <MapPinIcon style={{ width: 16, height: 16, color: '#718096' }} />
                        <span>{leadDetail.address}</span>
                      </div>
                    )}
                    {leadDetail.website && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <GlobeAltIcon style={{ width: 16, height: 16, color: '#718096' }} />
                        <a href={leadDetail.website} target="_blank" rel="noopener noreferrer" style={{ color: '#2B7FE0' }}>
                          {leadDetail.website}
                        </a>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <h3 style={{ fontSize: 14, fontWeight: 600, color: '#4a5568', marginBottom: 12 }}>Lead Details</h3>
                  <div style={{ display: 'grid', gap: 8, fontSize: 14 }}>
                    <div>Score: <strong>{leadDetail.lead_score}</strong></div>
                    <div>Category: <strong>{leadDetail.category_name || '-'}</strong></div>
                    <div>Source: <strong style={{ textTransform: 'capitalize' }}>{leadDetail.source || '-'}</strong></div>
                    <div>Created: <strong>{formatDate(leadDetail.created_at)}</strong></div>
                  </div>
                </div>
              </div>

              {/* Status Actions */}
              <div style={{ marginBottom: 24, padding: 16, background: '#f7fafc', borderRadius: 8 }}>
                <h3 style={{ fontSize: 14, fontWeight: 600, color: '#4a5568', marginBottom: 12 }}>Update Status</h3>
                <div style={{ display: 'flex', gap: 8 }}>
                  {['new', 'contacted', 'proposal', 'won', 'lost'].map(status => (
                    <button
                      key={status}
                      onClick={() => handleUpdateLeadStatus(leadDetail.id, status)}
                      disabled={leadDetail.lead_status === status}
                      style={{
                        padding: '8px 16px',
                        background: leadDetail.lead_status === status ? getStatusColor(status) : 'white',
                        color: leadDetail.lead_status === status ? 'white' : getStatusColor(status),
                        border: `1px solid ${getStatusColor(status)}`,
                        borderRadius: 6,
                        cursor: leadDetail.lead_status === status ? 'default' : 'pointer',
                        fontSize: 13,
                        fontWeight: 500,
                        textTransform: 'capitalize'
                      }}
                    >
                      {status}
                    </button>
                  ))}
                </div>
              </div>

              {/* Add Activity */}
              <div style={{ marginBottom: 24, padding: 16, background: '#f7fafc', borderRadius: 8 }}>
                <h3 style={{ fontSize: 14, fontWeight: 600, color: '#4a5568', marginBottom: 12 }}>Add Activity</h3>
                <div style={{ display: 'flex', gap: 12 }}>
                  <select
                    value={activityForm.activity_type}
                    onChange={(e) => setActivityForm({ ...activityForm, activity_type: e.target.value })}
                    style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
                  >
                    <option value="note_added">Note</option>
                    <option value="call">Call</option>
                    <option value="email">Email</option>
                    <option value="meeting">Meeting</option>
                    <option value="follow_up">Follow Up</option>
                  </select>
                  <input
                    type="text"
                    value={activityForm.description}
                    onChange={(e) => setActivityForm({ ...activityForm, description: e.target.value })}
                    placeholder="Enter description..."
                    style={{ flex: 1, padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
                  />
                  <button
                    onClick={handleAddActivity}
                    style={{
                      padding: '8px 16px',
                      background: '#2B7FE0',
                      color: 'white',
                      border: 'none',
                      borderRadius: 6,
                      cursor: 'pointer',
                      fontSize: 14
                    }}
                  >
                    Add
                  </button>
                </div>
              </div>

              {/* Activity Timeline */}
              <div style={{ marginBottom: 24 }}>
                <h3 style={{ fontSize: 14, fontWeight: 600, color: '#4a5568', marginBottom: 12 }}>Activity History</h3>
                {leadDetail.activities?.length > 0 ? (
                  <div style={{ display: 'grid', gap: 8 }}>
                    {leadDetail.activities.map(activity => (
                      <div key={activity.id} style={{
                        padding: 12,
                        background: '#f7fafc',
                        borderRadius: 6,
                        borderLeft: '3px solid #2B7FE0'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                          <span style={{ fontWeight: 500, textTransform: 'capitalize' }}>
                            {activity.activity_type.replace('_', ' ')}
                          </span>
                          <span style={{ fontSize: 12, color: '#718096' }}>
                            {formatDate(activity.created_at)}
                          </span>
                        </div>
                        <p style={{ fontSize: 14, color: '#4a5568' }}>{activity.description}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p style={{ color: '#718096', fontSize: 14 }}>No activities yet</p>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div style={{
              padding: 16,
              borderTop: '1px solid #e2e8f0',
              display: 'flex',
              justifyContent: 'space-between'
            }}>
              <button
                onClick={() => handleDeleteLead(leadDetail.id)}
                style={{
                  padding: '10px 16px',
                  background: '#fee2e2',
                  color: '#991b1b',
                  border: 'none',
                  borderRadius: 6,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  fontSize: 14
                }}
              >
                <TrashIcon style={{ width: 16, height: 16 }} />
                Delete Lead
              </button>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={() => {
                    setSelectedLead(leadDetail);
                    setShowCalculator(true);
                  }}
                  style={{
                    padding: '10px 16px',
                    background: '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: 6,
                    cursor: 'pointer',
                    fontSize: 14,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6
                  }}
                >
                  <CalculatorIcon style={{ width: 16, height: 16 }} />
                  Quote Calculator
                </button>
                <button
                  onClick={() => setShowLeadModal(false)}
                  style={{
                    padding: '10px 20px',
                    background: '#e2e8f0',
                    color: '#1f2937',
                    border: 'none',
                    borderRadius: 6,
                    cursor: 'pointer',
                    fontSize: 14
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quote Creation Modal */}
      {showQuoteModal && selectedLead && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001,
          padding: 20
        }}>
          <div style={{
            background: 'white',
            borderRadius: 12,
            width: '100%',
            maxWidth: 700,
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <div style={{
              padding: 24,
              borderBottom: '1px solid #e2e8f0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{
                  width: 48,
                  height: 48,
                  borderRadius: 10,
                  background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <DocumentTextIcon style={{ width: 24, height: 24, color: 'white' }} />
                </div>
                <div>
                  <h3 style={{ fontSize: 18, fontWeight: 600 }}>Create Quote</h3>
                  <p style={{ fontSize: 13, color: '#718096' }}>for {selectedLead.name}</p>
                </div>
              </div>
              <button
                onClick={() => setShowQuoteModal(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer' }}
              >
                <XMarkIcon style={{ width: 24, height: 24 }} />
              </button>
            </div>

            <div style={{ padding: 24 }}>
              {/* Service Lines */}
              <div style={{ marginBottom: 24 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <h4 style={{ fontSize: 14, fontWeight: 600 }}>Services</h4>
                  <button
                    onClick={addServiceLine}
                    style={{
                      padding: '6px 12px',
                      background: '#f7fafc',
                      border: '1px solid #e2e8f0',
                      borderRadius: 4,
                      cursor: 'pointer',
                      fontSize: 13,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4
                    }}
                  >
                    <PlusIcon style={{ width: 14, height: 14 }} />
                    Add Line
                  </button>
                </div>

                {quoteForm.services.map((service, index) => (
                  <div key={index} style={{ display: 'grid', gridTemplateColumns: '3fr 1fr 1fr auto', gap: 8, marginBottom: 8 }}>
                    <input
                      type="text"
                      value={service.description}
                      onChange={(e) => updateServiceLine(index, 'description', e.target.value)}
                      placeholder="Service description"
                      style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 14 }}
                    />
                    <input
                      type="number"
                      value={service.quantity}
                      onChange={(e) => updateServiceLine(index, 'quantity', e.target.value)}
                      placeholder="Qty"
                      min="1"
                      style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 14 }}
                    />
                    <input
                      type="number"
                      value={service.unit_price}
                      onChange={(e) => updateServiceLine(index, 'unit_price', e.target.value)}
                      placeholder="Price"
                      min="0"
                      step="0.01"
                      style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 14 }}
                    />
                    {quoteForm.services.length > 1 && (
                      <button
                        onClick={() => removeServiceLine(index)}
                        style={{
                          padding: '8px',
                          background: '#fee2e2',
                          border: 'none',
                          borderRadius: 4,
                          cursor: 'pointer'
                        }}
                      >
                        <XMarkIcon style={{ width: 16, height: 16, color: '#991b1b' }} />
                      </button>
                    )}
                  </div>
                ))}
              </div>

              {/* Discount and Tax */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 8 }}>Discount %</label>
                  <input
                    type="number"
                    value={quoteForm.discount_percent}
                    onChange={(e) => setQuoteForm({ ...quoteForm, discount_percent: e.target.value })}
                    min="0"
                    max="100"
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 14 }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 8 }}>Tax %</label>
                  <input
                    type="number"
                    value={quoteForm.tax_percent}
                    onChange={(e) => setQuoteForm({ ...quoteForm, tax_percent: e.target.value })}
                    min="0"
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 14 }}
                  />
                </div>
              </div>

              {/* Valid Until */}
              <div style={{ marginBottom: 24 }}>
                <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 8 }}>Valid Until</label>
                <input
                  type="date"
                  value={quoteForm.valid_until}
                  onChange={(e) => setQuoteForm({ ...quoteForm, valid_until: e.target.value })}
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 14 }}
                />
              </div>

              {/* Notes */}
              <div style={{ marginBottom: 24 }}>
                <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 8 }}>Client Notes</label>
                <textarea
                  value={quoteForm.client_notes}
                  onChange={(e) => setQuoteForm({ ...quoteForm, client_notes: e.target.value })}
                  placeholder="Notes to include on the quote..."
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: 4,
                    fontSize: 14,
                    minHeight: 80,
                    resize: 'vertical'
                  }}
                />
              </div>

              {/* Totals */}
              <div style={{ background: '#f7fafc', padding: 16, borderRadius: 8 }}>
                {(() => {
                  const totals = calculateQuoteTotal();
                  return (
                    <div style={{ display: 'grid', gap: 8 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Subtotal:</span>
                        <span>{formatCurrency(totals.subtotal)}</span>
                      </div>
                      {totals.discount > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', color: '#10b981' }}>
                          <span>Discount:</span>
                          <span>-{formatCurrency(totals.discount)}</span>
                        </div>
                      )}
                      {totals.tax > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span>Tax:</span>
                          <span>{formatCurrency(totals.tax)}</span>
                        </div>
                      )}
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: 18, paddingTop: 8, borderTop: '1px solid #e2e8f0' }}>
                        <span>Total:</span>
                        <span style={{ color: '#10b981' }}>{formatCurrency(totals.total)}</span>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>

            <div style={{ padding: 16, borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                onClick={() => setShowQuoteModal(false)}
                style={{
                  padding: '10px 20px',
                  background: '#e2e8f0',
                  color: '#1f2937',
                  border: 'none',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontSize: 14
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateQuote}
                style={{
                  padding: '10px 24px',
                  background: '#8b5cf6',
                  color: 'white',
                  border: 'none',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontSize: 14,
                  fontWeight: 500
                }}
              >
                Create Quote
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Search Area Modal */}
      {showAreaModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: 20
        }}>
          <div style={{
            background: 'white',
            borderRadius: 12,
            width: '100%',
            maxWidth: 500
          }}>
            <div style={{
              padding: 24,
              borderBottom: '1px solid #e2e8f0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{ fontSize: 18, fontWeight: 600 }}>Add Search Area</h3>
              <button
                onClick={() => setShowAreaModal(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer' }}
              >
                <XMarkIcon style={{ width: 24, height: 24 }} />
              </button>
            </div>

            <div style={{ padding: 24 }}>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 8 }}>Name *</label>
                <input
                  type="text"
                  value={areaForm.name}
                  onChange={(e) => setAreaForm({ ...areaForm, name: e.target.value })}
                  placeholder="e.g., Downtown Miami"
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 8 }}>State *</label>
                  <input
                    type="text"
                    value={areaForm.state}
                    onChange={(e) => setAreaForm({ ...areaForm, state: e.target.value.toUpperCase().slice(0, 2) })}
                    placeholder="FL"
                    maxLength={2}
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 8 }}>County</label>
                  <input
                    type="text"
                    value={areaForm.county}
                    onChange={(e) => setAreaForm({ ...areaForm, county: e.target.value })}
                    placeholder="Miami-Dade"
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 8 }}>Latitude *</label>
                  <input
                    type="number"
                    value={areaForm.lat}
                    onChange={(e) => setAreaForm({ ...areaForm, lat: e.target.value })}
                    placeholder="25.7617"
                    step="0.0001"
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 8 }}>Longitude *</label>
                  <input
                    type="number"
                    value={areaForm.lng}
                    onChange={(e) => setAreaForm({ ...areaForm, lng: e.target.value })}
                    placeholder="-80.1918"
                    step="0.0001"
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 8 }}>Radius (mi)</label>
                  <input
                    type="number"
                    value={areaForm.radius_miles}
                    onChange={(e) => setAreaForm({ ...areaForm, radius_miles: e.target.value })}
                    min="1"
                    max="25"
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
                  />
                </div>
              </div>

              <p style={{ fontSize: 12, color: '#718096', marginTop: 8 }}>
                Tip: Use Google Maps to find lat/lng coordinates for your target area
              </p>
            </div>

            <div style={{ padding: 16, borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                onClick={() => setShowAreaModal(false)}
                style={{
                  padding: '10px 20px',
                  background: '#e2e8f0',
                  color: '#1f2937',
                  border: 'none',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontSize: 14
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateSearchArea}
                style={{
                  padding: '10px 24px',
                  background: '#2B7FE0',
                  color: 'white',
                  border: 'none',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontSize: 14,
                  fontWeight: 500
                }}
              >
                Create Area
              </button>
            </div>
          </div>
        </div>
      )}

{showContactModal && contactingLead && (
        <ContactModal
          lead={contactingLead}
          currentUser={user}
          onClose={() => { setShowContactModal(false); setContactingLead(null); }}
          onSuccess={() => { fetchLeads(); fetchPipelineCounts(); }}
        />
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default LeadFinder;
